import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Edit, Trash2, Plus, RefreshCw, Cloud, CloudOff, Upload } from 'lucide-react';
import { useSequenceStore } from '../store/sequenceStore';
import { testS3Connection } from '../services/sequenceService';
import { playSequenceName, getAudioContext, isAndroid, isIOS, isMac } from '../services/pollyService';
import PasswordPrompt from '../components/PasswordPrompt';
import { PUBLIC_SEQUENCE_EDIT_PASSWORD } from '../config/constants';

function Catalog() {
  const navigate = useNavigate();
  const { sequences, deleteSequence, setSelectedSequence, initializeStore, user, updateSequence } = useSequenceStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const [passwordPromptSequence, setPasswordPromptSequence] = useState<any | null>(null);
  const [migratingSequenceId, setMigratingSequenceId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    console.log('Catalog component mounted');
  }, []);

  // Filter sequences and ensure unique IDs
  const uniqueSequences = [...new Map(sequences.map(seq => [seq.id, seq])).values()];
  const publicSequences = uniqueSequences.filter((seq) => seq.isPublic === true);
  const privateSequences = uniqueSequences.filter((seq) => !seq.isPublic);
  const cloudSequences = privateSequences.filter(seq => seq.cloudId);
  const localSequences = privateSequences.filter(seq => !seq.cloudId);

  const handlePlay = async (sequence: any) => {
    try {
      setErrorMessage(null); // Clear any previous errors

          // Initialize and resume AudioContext in response to user interaction before any async calls
      if (isIOS || isAndroid || isMac) {
        const ctx = getAudioContext();
        await ctx.resume(); // Ensure the AudioContext is resumed
      }
      await playSequenceName(sequence);
      setSelectedSequence(sequence);
      navigate('player');
    } catch (error) {
      // Log detailed error information
      console.error('Error playing sequence name:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      });
      
      // Set a user-friendly error message
      setErrorMessage(`Failed to play sequence: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      
      // Still navigate to player as fallback
      setSelectedSequence(sequence);
      navigate('player');
    }
  };

  const handleEdit = (sequence: any) => {
    if (sequence.isPublic) {
      setPasswordPromptSequence(sequence);
    } else {
      setSelectedSequence(sequence);
      navigate('design');
    }
  };

  const handlePasswordSubmit = (password: string) => {
    if (password === PUBLIC_SEQUENCE_EDIT_PASSWORD) {
      setSelectedSequence(passwordPromptSequence);
      setPasswordPromptSequence(null);
      navigate('design');
    } else {
      alert('Incorrect password');
    }
  };

  const handlePasswordCancel = () => {
    setPasswordPromptSequence(null);
  };

  const handleMigrateToCloud = async (sequence: any) => {
    if (!user) {
      alert('Please log in to migrate sequences to the cloud');
      return;
    }

    setMigratingSequenceId(sequence.id);
    try {
      // Create a new sequence object with cloud storage properties
      const cloudSequence = {
        ...sequence,
        cloudId: null // This will be set when saved to S3
      };

      // Update the sequence in the store, which will handle saving to S3
      await updateSequence(cloudSequence, true);
      
      // Refresh the store to get updated sequence list
      await initializeStore();
    } catch (error) {
      console.error('Error migrating sequence to cloud:', error);
      alert('Failed to migrate sequence to cloud storage');
    } finally {
      setMigratingSequenceId(null);
    }
  };

  const handleRefresh = async () => {
    console.log('Manually refreshing sequences...');
    setIsRefreshing(true);
    setConnectionStatus('Testing S3 connection...');
    
    try {
      const connectionSuccessful = await testS3Connection();
      setConnectionStatus(connectionSuccessful 
        ? 'S3 connection successful' 
        : 'S3 connection failed - check console for details');
      
      await initializeStore();
    } catch (error) {
      console.error('Error during refresh:', error);
      setConnectionStatus('Error during refresh - check console for details');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sequence Catalog</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`flex items-center space-x-2 px-2 py-2 ${
              isRefreshing ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'
            } text-gray-700 rounded-lg`}
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          <button
            onClick={() => {setSelectedSequence(null); navigate('design')}}
            className="flex items-center space-x-1 px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            <span>New Sequence</span>
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-800">
          {errorMessage}
        </div>
      )}

      {connectionStatus && (
        <div className={`mb-4 p-3 rounded-lg ${
          connectionStatus.includes('failed') || connectionStatus.includes('Error')
            ? 'bg-red-100 text-red-800'
            : connectionStatus.includes('successful')
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
        }`}>
          {connectionStatus}
        </div>
      )}

      <div className="space-y-8">
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Public Sequences ({publicSequences.length})
          </h2>
          {publicSequences.length > 0 ? (
            <div className="bg-white rounded-lg shadow divide-y">
              {publicSequences.map((sequence) => (
                <SequenceItem
                  key={`public-${sequence.id}`}
                  sequence={sequence}
                  onPlay={handlePlay}
                  onEdit={handleEdit}
                  onDelete={null}
                  onMigrateToCloud={null}
                  isMigrating={false}
                  showCloudIcon={false}
                  isLoggedIn={!!user}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              No public sequences available
            </div>
          )}
        </section>

        {localSequences.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Local Sequences ({localSequences.length})
            </h2>
            <div className="bg-white rounded-lg shadow divide-y">
              {localSequences.map((sequence) => (
                <SequenceItem
                  key={`local-${sequence.id}`}
                  sequence={sequence}
                  onPlay={handlePlay}
                  onEdit={handleEdit}
                  onDelete={deleteSequence}
                  onMigrateToCloud={handleMigrateToCloud}
                  isMigrating={migratingSequenceId === sequence.id}
                  showCloudIcon={true}
                  isLoggedIn={!!user}
                />
              ))}
            </div>
          </section>
        )}

        {cloudSequences.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              My Sequences ({cloudSequences.length})
            </h2>
            <div className="bg-white rounded-lg shadow divide-y">
              {cloudSequences.map((sequence) => (
                <SequenceItem
                  key={`cloud-${sequence.id}`}
                  sequence={sequence}
                  onPlay={handlePlay}
                  onEdit={handleEdit}
                  onDelete={deleteSequence}
                  onMigrateToCloud={null}
                  isMigrating={false}
                  showCloudIcon={true}
                  isLoggedIn={!!user}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {passwordPromptSequence && (
        <PasswordPrompt 
          isOpen={true}
          onSubmit={handlePasswordSubmit}
          onCancel={handlePasswordCancel}
        />
      )}
    </div>
  );
}

interface SequenceItemProps {
  sequence: any;
  onPlay: (sequence: any) => void;
  onEdit: (sequence: any) => void;
  onDelete: ((id: string) => void) | null;
  onMigrateToCloud: ((sequence: any) => void) | null;
  isMigrating: boolean;
  showCloudIcon: boolean;
  isLoggedIn: boolean;
}

function SequenceItem({ 
  sequence, 
  onPlay, 
  onEdit, 
  onDelete, 
  onMigrateToCloud,
  isMigrating,
  showCloudIcon,
  isLoggedIn
}: SequenceItemProps) {
  return (
    <div className="p-4 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        {showCloudIcon && (
          sequence.cloudId ? (
            <Cloud size={16} className="text-blue-500" />
          ) : (
            <CloudOff size={16} className="text-gray-400" />
          )
        )}
        <span className="text-lg text-gray-700">{sequence.name}</span>
      </div>
      <div className="flex items-center space-x-2">
        {onMigrateToCloud && !sequence.cloudId && isLoggedIn && (
          <button
            onClick={() => onMigrateToCloud(sequence)}
            disabled={isMigrating}
            className={`p-2 ${
              isMigrating 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:bg-blue-50'
            } rounded-full`}
            title="Migrate to cloud storage"
          >
            <Upload size={20} className={isMigrating ? 'animate-pulse' : ''} />
          </button>
        )}
        <button
          onClick={() => onPlay(sequence)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
          title="Play"
        >
          <Play size={20} />
        </button>
        <button
          onClick={() => onEdit(sequence)}
          className="p-2 text-gray-600 hover:bg-gray-50 rounded-full"
          title="Edit"
        >
          <Edit size={20} />
        </button>
        {onDelete && (
          <button
            onClick={() => onDelete(sequence.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-full"
            title="Delete"
          >
            <Trash2 size={20} />
          </button>
        )}
      </div>
    </div>
  );
}

export default Catalog;